import type { CSSProperties, ReactNode } from "react";
import { useNavigate } from "react-router";
import { Breadcrumb, Avatar, Pill, ConnectButton } from "../primitives";
import type { LibraryNodeDetail } from "../../../lib/types";
import type { EntityTypeDefinition } from "../../../lib/types";


interface TableHeaderProps {
  table: LibraryNodeDetail;
  entityType: EntityTypeDefinition;
  rowCount: number;
  parentAgent?: { id: string; name: string; persona?: string } | null;
  actionMenu?: ReactNode;
}

export function TableHeader({
  table,
  entityType,
  rowCount,
  parentAgent,
  actionMenu,
}: TableHeaderProps) {
  const navigate = useNavigate();

  const breadcrumbSegments = [
    ...(table._breadcrumb ?? []).map((b) => ({
      label: b.name,
      onClick: b.type === "agent" ? () => navigate(`/agents/${b.id}`) : undefined,
    })),
    { label: table.name },
  ];

  const plural = entityType.plural ?? entityType.slug + "s";
  const countLabel = `${rowCount} of ${rowCount} open ${plural}`;

  const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 14,
    marginBottom: 12,
    borderBottom: "0.5px solid var(--color-border-tertiary)",
  };

  return (
    <div>
      <Breadcrumb segments={breadcrumbSegments} />

      <div style={{ ...headerStyle, marginTop: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {table.name}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
            {parentAgent && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Avatar
                  persona={(parentAgent.persona as any) ?? "teal"}
                  size="xs"
                  name={parentAgent.name}
                />
                <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  Owned by {parentAgent.name}
                </span>
              </div>
            )}
            <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>·</span>
            <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
              {countLabel}
            </span>
            {entityType.is_scope && (
              <>
                <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>·</span>
                <Pill variant="blue" icon="focus-2">Scope</Pill>
              </>
            )}
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {actionMenu}
          <ConnectButton agentId={table.id} />
        </div>
      </div>
    </div>
  );
}
